// Mon Profil - Profil public du vendeur
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User, Star, MapPin, Calendar, Package, Eye, Heart, MessageCircle,
  Edit, Camera, Share2, CheckCircle, Shield, Award, ExternalLink, Upload, X, Info, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BIO_MAX_LENGTH = 150; // Limite Instagram

// Composant modal pour upload d'image
function ImageUploadModal({ isOpen, onClose, type, onUpload, saving }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  
  const dimensions = type === 'cover' 
    ? { width: 1584, height: 396, label: 'Image de couverture', ratio: '4:1' }
    : { width: 400, height: 400, label: 'Photo de profil', ratio: '1:1' };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Sélectionne un fichier image (JPG, PNG, WebP)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop lourde (max 5 Mo)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = () => {
    if (!preview) return;
    onUpload(preview);
  };

  useEffect(() => {
    if (!isOpen) setPreview(null);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{dimensions.label}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl text-sm">
            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Dimensions recommandées</p>
              <p className="text-blue-700">
                {dimensions.width} × {dimensions.height} pixels (ratio {dimensions.ratio})
              </p>
            </div>
          </div>

          {preview ? (
            <div className="space-y-3">
              <div 
                className={`rounded-xl overflow-hidden border-2 border-dashed border-gray-300 ${
                  type === 'cover' ? 'aspect-[4/1]' : 'aspect-square max-w-[200px] mx-auto'
                }`}
              >
                <img src={preview} alt="Aperçu" className="w-full h-full object-cover" />
              </div>
              <button 
                onClick={() => { setPreview(null); fileInputRef.current?.click(); }}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Choisir une autre image
              </button>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors ${
                type === 'cover' ? 'aspect-[4/1]' : 'aspect-square max-w-[200px] mx-auto'
              } flex flex-col items-center justify-center`}
            >
              <Upload size={32} className="text-gray-400 mb-2" />
              <p className="text-gray-600 text-sm">Clique pour sélectionner une image</p>
              <p className="text-gray-400 text-xs mt-1">ou glisse-dépose ici</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          <p className="text-xs text-gray-500 text-center">
            Formats acceptés : JPG, PNG, WebP (max 5 Mo)
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleUpload} 
            disabled={!preview || saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Enregistrement...
              </>
            ) : 'Enregistrer'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Composant modal pour éditer la bio
function EditBioModal({ isOpen, onClose, currentBio, onSave, saving }) {
  const [bio, setBio] = useState(currentBio || '');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setBio(currentBio || '');
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, currentBio]);

  if (!isOpen) return null;

  const remaining = BIO_MAX_LENGTH - bio.length;

  return (
    <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Ta bio</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <textarea
              ref={textareaRef}
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX_LENGTH))}
              placeholder="Présente-toi en quelques mots..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none text-gray-900"
              data-testid="bio-textarea"
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">Visible sur ton profil public</p>
              <p className={`text-xs font-medium ${remaining < 20 ? 'text-orange-500' : 'text-gray-400'}`}>
                {remaining} caractères restants
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-2">Exemples de bio :</p>
            <div className="space-y-2 text-sm text-gray-600">
              <p className="italic">"Passionné de bricolage depuis 15 ans. Je vends du matériel pro à prix doux."</p>
              <p className="italic">"Artisan plombier à Lyon. Vente de surplus de chantier 🔧"</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={() => onSave(bio)}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Enregistrement...
              </>
            ) : 'Enregistrer'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default function ProfilPage() {
  const { user, initials } = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(null);
  const [showBioModal, setShowBioModal] = useState(false);
  
  // Données du profil
  const [profileData, setProfileData] = useState({
    bio: '',
    cover_photo: null,
    profile_photo: null,
    city: '',
    postal_code: '',
    username: null,
  });
  
  // Stats
  const [stats, setStats] = useState({
    rating: 0,
    reviewCount: 0,
    annoncesCount: 0,
    ventesCount: 0,
    followers: 0,
    responseTime: '-',
  });

  const [reviews, setReviews] = useState([]);

  // Charger le profil depuis le backend
  useEffect(() => {
    const loadProfile = async () => {
      const token = localStorage.getItem('ecosystem_token');
      
      if (!token || user?.isDemo) {
        // Mode démo
        setProfileData({
          bio: 'Passionné de bricolage et rénovation. Je vends régulièrement du matériel de qualité à prix raisonnable.',
          cover_photo: null,
          profile_photo: null,
          city: 'Lyon',
          postal_code: '69001',
          username: 'jean_dupont',
        });
        setStats({
          rating: 4.8,
          reviewCount: 24,
          annoncesCount: 5,
          ventesCount: 8,
          followers: 12,
          responseTime: '< 1 heure',
        });
        setReviews([
          { id: 1, author: 'Marie L.', rating: 5, text: 'Vendeur très sérieux, article conforme à la description. Je recommande !', date: 'Il y a 3 jours' },
          { id: 2, author: 'Pierre D.', rating: 5, text: 'Transaction rapide et efficace. Merci !', date: 'Il y a 1 semaine' },
          { id: 3, author: 'Sophie M.', rating: 4, text: 'Bon produit, livraison un peu longue mais vendeur réactif.', date: 'Il y a 2 semaines' },
        ]);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/ecosystem/profile/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const profile = await response.json();
          
          setProfileData({
            bio: profile.bio || '',
            cover_photo: profile.cover_photo || null,
            profile_photo: profile.profile_photo || null,
            city: profile.city || '',
            postal_code: profile.postal_code || '',
            username: profile.username || null,
          });
          
          // Stats vides pour les vrais utilisateurs (à connecter plus tard)
          setStats({
            rating: 0,
            reviewCount: 0,
            annoncesCount: 0,
            ventesCount: 0,
            followers: profile.followers_count || 0,
            responseTime: '-',
          });
          setReviews([]);
        }
      } catch (error) {
        console.error('Erreur chargement profil:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user !== null) {
      loadProfile();
    }
  }, [user]);

  // Sauvegarder une modification
  const saveProfile = async (updates) => {
    const token = localStorage.getItem('ecosystem_token');
    if (!token) {
      toast.error('Tu dois être connecté');
      return false;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ecosystem/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setProfileData(prev => ({ ...prev, ...updates }));
        return true;
      } else {
        toast.error('Erreur lors de la sauvegarde');
        return false;
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur de connexion');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Handlers
  const handleImageUpload = async (imageData) => {
    const field = showUploadModal === 'cover' ? 'cover_photo' : 'profile_photo';
    const success = await saveProfile({ [field]: imageData });
    if (success) {
      toast.success(showUploadModal === 'cover' ? 'Couverture mise à jour !' : 'Photo de profil mise à jour !');
      setShowUploadModal(null);
    }
  };

  const handleBioSave = async (newBio) => {
    const success = await saveProfile({ bio: newBio });
    if (success) {
      toast.success('Bio mise à jour !');
      setShowBioModal(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Lien copié !');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const isDemo = user?.isDemo;
  const displayName = user?.name || 'Utilisateur';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Modals */}
      <ImageUploadModal 
        isOpen={showUploadModal !== null}
        onClose={() => setShowUploadModal(null)}
        type={showUploadModal}
        onUpload={handleImageUpload}
        saving={saving}
      />
      <EditBioModal
        isOpen={showBioModal}
        onClose={() => setShowBioModal(false)}
        currentBio={profileData.bio}
        onSave={handleBioSave}
        saving={saving}
      />

      {/* Bouton Voir Profil Public */}
      {!isDemo && profileData.username && (
        <Link 
          to={`/reseau/profil/${profileData.username}`}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-colors group"
          data-testid="view-public-profile-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Eye size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Aperçu de ton profil public</p>
              <p className="text-sm text-gray-500">Vois comment les autres membres te voient sur le réseau</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-blue-600 font-medium">
            <span className="hidden sm:inline">Voir mon profil</span>
            <ExternalLink size={18} />
          </div>
        </Link>
      )}

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Cover - Format LinkedIn 4:1 (1584x396) */}
        <div 
          className={`h-36 sm:h-44 relative ${!isDemo ? 'cursor-pointer group' : ''}`}
          style={profileData.cover_photo 
            ? { backgroundImage: `url(${profileData.cover_photo})`, backgroundSize: 'cover', backgroundPosition: 'center' } 
            : { backgroundColor: '#1a2a4a' }
          }
          data-testid="cover-upload-area"
        >
          {/* Si pas d'image de couverture : affichage par défaut */}
          {!profileData.cover_photo && (
            <>
              {/* Texte centré */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h2 style={{ color: '#ffffff', fontWeight: 800, fontSize: '22px', marginBottom: '8px' }}>
                  Bienvenue chez toi. ✨
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', maxWidth: '400px' }}>
                  Configure ton profil de magicien(ne) pour te présenter à la communauté de confiance.
                </p>
              </div>
              
              {/* Bouton discret en haut à droite */}
              {!isDemo && (
                <div className="absolute top-3 right-3 flex flex-col items-end">
                  <button
                    onClick={() => setShowUploadModal('cover')}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#ffffff',
                      fontSize: '12px',
                      borderRadius: '20px',
                      padding: '6px 14px',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                  >
                    📷 Ajouter une image de couverture
                  </button>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginTop: '4px' }}>
                    (1584 × 396 px)
                  </span>
                </div>
              )}
            </>
          )}
          
          {/* Si image de couverture présente : bouton modifier */}
          {profileData.cover_photo && !isDemo && (
            <div 
              onClick={() => setShowUploadModal('cover')}
              className="absolute top-3 right-3 p-2 bg-white/20 group-hover:bg-white/30 rounded-lg text-white backdrop-blur-sm flex items-center gap-2 text-sm cursor-pointer"
            >
              <Camera size={18} />
              <span className="hidden sm:inline">Modifier</span>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="px-6 pb-6 -mt-12">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Avatar */}
            <div className="relative">
              {profileData.profile_photo ? (
                <div 
                  className="w-24 h-24 rounded-2xl border-4 border-white shadow-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${profileData.profile_photo})` }}
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg">
                  {initials}
                </div>
              )}
              {stats.reviewCount > 0 && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <CheckCircle size={16} className="text-white" />
                </div>
              )}
              {!isDemo && (
                <button 
                  onClick={() => setShowUploadModal('avatar')}
                  className="absolute -bottom-1 -left-1 w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center border-2 border-white"
                  data-testid="avatar-upload-btn"
                >
                  <Camera size={12} className="text-gray-600" />
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">{displayName}</h1>
                {stats.reviewCount > 0 && (
                  <span className="flex items-center gap-1 text-yellow-500">
                    <Star size={16} fill="currentColor" />
                    <span className="text-gray-700 font-medium">{stats.rating}</span>
                    <span className="text-gray-400 text-sm">({stats.reviewCount})</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                {profileData.city && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {profileData.city}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  Membre depuis {isDemo ? 'Mars 2024' : 'Aujourd\'hui'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleShare} className="gap-2">
                <Share2 size={16} />
                Partager
              </Button>
              {!isDemo && (
                <Button 
                  onClick={() => setShowBioModal(true)}
                  variant="outline" 
                  className="gap-2 border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  <Edit size={16} />
                  Modifier
                </Button>
              )}
            </div>
          </div>

          {/* Bio Section */}
          <div className="mt-6">
            {profileData.bio ? (
              <div 
                onClick={() => !isDemo && setShowBioModal(true)}
                className={`p-4 bg-gray-50 rounded-xl ${!isDemo ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
              >
                <p className="text-gray-700 leading-relaxed">{profileData.bio}</p>
                <p className="text-xs text-gray-400 mt-2">{profileData.bio.length}/{BIO_MAX_LENGTH} caractères</p>
              </div>
            ) : !isDemo ? (
              <button
                onClick={() => setShowBioModal(true)}
                className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50/50 transition-colors text-left"
                data-testid="add-bio-btn"
              >
                <p className="font-medium">Ajoute une bio pour te présenter aux autres membres</p>
              </button>
            ) : null}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-yellow-500 mb-1">
                <Star size={16} fill={stats.reviewCount > 0 ? "currentColor" : "none"} />
                <span className="font-semibold text-gray-900">{stats.rating > 0 ? stats.rating : '-'}</span>
              </div>
              <p className="text-xs text-gray-500">{stats.reviewCount} avis</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">{stats.annoncesCount}</p>
              <p className="text-xs text-gray-500">Annonces</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">{stats.ventesCount}</p>
              <p className="text-xs text-gray-500">Ventes</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">{stats.followers}</p>
              <p className="text-xs text-gray-500">Abonnés</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-900">{stats.responseTime}</p>
              <p className="text-xs text-gray-500">Réponse</p>
            </div>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Badges</h2>
        
        {isDemo ? (
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
              <Shield size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Identité vérifiée</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full">
              <Award size={18} className="text-green-600" />
              <span className="text-sm font-medium text-green-700">Vendeur de confiance</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full">
              <Star size={18} className="text-orange-600" />
              <span className="text-sm font-medium text-orange-700">Top vendeur</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Award size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Tu n'as pas encore de badge.</p>
            <p className="text-sm text-gray-400 mt-1">Complète ton profil et fais des ventes pour en obtenir !</p>
          </div>
        )}
      </div>

      {/* Avis */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Derniers avis</h2>
          {reviews.length > 0 && (
            <span className="text-sm text-gray-500">{stats.reviewCount} avis au total</span>
          )}
        </div>

        {reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium flex-shrink-0">
                  {review.author.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{review.author}</span>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">{review.date}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{review.text}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageCircle size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucun avis pour le moment</p>
            <p className="text-sm text-gray-400 mt-1">Les avis apparaîtront après tes premières ventes</p>
          </div>
        )}
      </div>
    </div>
  );
}
